import { FACET_KEYS, type FacetKey } from "./assessment-types";

export const RESULT_TYPE_KEYS = [
  "internet_unscarred",
  "emotional_power_saver",
  "cynical_watcher",
  "natural_elite",
  "human_chess_player",
  "premature_fossil",
  "permanent_victim",
  "logic_pressure_cooker",
  "approval_reformer",
  "early_rogai",
  "humanity_abandoned",
  "elite_lecturer",
  "counterattack_defense",
  "natural_controller",
  "flame_commentator",
  "zero_margin_defense",
  "good_old_days_reserve",
  "instant_mount",
  "emotion_cost_manager",
  "online_executioner",
  "personal_exception",
] as const;

export type ResultTypeKey = (typeof RESULT_TYPE_KEYS)[number];

export type ResultDefinition = Readonly<{
  key: ResultTypeKey;
  name: string;
  comments: readonly [string, string, string];
  reason: string;
  behaviors: readonly string[];
  recoveryActions: readonly string[];
  imageAlt: string;
}>;

const definition = (
  key: ResultTypeKey,
  name: string,
  comments: readonly [string, string, string],
  reason: string,
  behaviors: readonly [string, string],
  recoveryActions: readonly [string, ...string[]],
  imageAlt: string,
): ResultDefinition => ({
  key,
  name,
  comments,
  reason,
  behaviors,
  recoveryActions,
  imageAlt,
});

// Schema v1 share URLs are long-lived. Do not mutate this catalog in place;
// add a new version and keep v1 available when result content changes.
export const RESULT_DEFINITIONS_V1 = [
  definition(
    "internet_unscarred",
    "インターネット無傷の民",
    [
      "Xを見ているのに、まだ人間を信じています。現代ではほぼ保護対象です。",
      "疑う前に話を聞ける余白が残っています。その余白、タイムラインに吸われないよう保管してください。",
      "正しさで殴るより、いったん画面を閉じる選択ができています。かなり希少な操作です。",
    ],
    "8つの行動パターンの現在値がすべて50未満で、回答全体にも強い偏りが見られませんでした。これは善人認定ではありません。",
    [
      "相手の事情を確認してから反応する",
      "対立的な投稿を見ても拡散せず距離を置く",
    ],
    ["今の反応の余白を守るため、疲れている日は判断や投稿を翌日に回す"],
    "穏やかな表情でスマートフォンを置く人物と、傷のない盾を描いたイラスト",
  ),
  definition(
    "emotional_power_saver",
    "心の省エネモード",
    [
      "他人の事情を読む前に、心のバッテリー節約が始まっています。",
      "共感のアプリだけバックグラウンド停止中。必要な場面では手動で起動を。",
      "弱音を聞くと省電力表示。まず一問だけ事情を聞くと充電が戻りそうです。",
    ],
    "相手の事情を想像する項目より、弱さを甘えとして早く処理する回答が目立ちました。",
    ["相談を受けた直後に結論や評価を返す", "相手の説明が終わる前に距離を置く"],
    ["評価を伝える前に「何が一番つらい？」と一つだけ質問する"],
    "省電力マークのついたハートを抱え、会話から少し距離を取る人物のイラスト",
  ),
  definition(
    "cynical_watcher",
    "冷笑ウォッチャー",
    [
      "応援ボタンより先に、失敗待ちの観客席を確保しています。",
      "挑戦の予告を見ると検証班が即出動。成功後の拍手はまだ読み込み中です。",
      "善意の裏読みは高速ですが、表に書いてある努力もたまには読んでください。",
    ],
    "善意や挑戦をそのまま受け取るより、裏の目的や失敗の可能性を先に探す回答が高く出ました。",
    [
      "人の挑戦を結果が出るまで評価しない",
      "立派な発言を見ると意図や得を先に疑う",
    ],
    ["裏読みを始めたら、確認できる事実と自分の推測を一行ずつ分けて書く"],
    "観客席から双眼鏡で他人の挑戦を眺める人物と、冷めた吹き出しのイラスト",
  ),
  definition(
    "natural_elite",
    "ナチュラル選民",
    [
      "順番待ちの列に並びながら、自分専用の優先レーンを探しています。",
      "ルールは理解済み。ただし自分の実績欄だけ例外申請に添付されます。",
      "普通の対応をされると不具合判定。期待値の設定画面を一度ご確認ください。",
    ],
    "同じ条件を受け入れるより、自分の経験や立場なら特別対応が妥当だと考える回答が目立ちました。",
    [
      "断られたときに立場や利用歴を示す",
      "通常対応を軽く扱われたことだと受け取る",
    ],
    ["要望を伝える前に、他の人にも同じ例外を認められるか考える"],
    "優先レーンを探しながら受付に会員証を見せる人物を描いたイラスト",
  ),
  definition(
    "human_chess_player",
    "人間チェスプレイヤー",
    [
      "会話相手がいつの間にか駒として配置されています。",
      "お願いは丁寧でも、相手の退路だけ盤面から消えています。",
      "人の反応を三手先まで読む前に、相手の「嫌です」も有効手として登録を。",
    ],
    "率直に相談するより、情報や状況を調整して相手が断りにくい進め方を選ぶ傾向が出ました。",
    [
      "必要な情報を小分けにして先に了承を取る",
      "急ぎや関係性を使って依頼を通す",
    ],
    ["依頼時に目的、負担、断っても不利益がないことを同時に伝える"],
    "チェス盤の前で会話の吹き出しを駒のように動かす人物のイラスト",
  ),
  definition(
    "premature_fossil",
    "化石化フライング",
    [
      "新提案の中身より先に、地層へ分類しています。",
      "経験は武器ですが、最新版への更新通知をずっと保留しています。",
      "「前に失敗した」が万能パスワード化。今回は何が違うかも確認を。",
    ],
    "新しい考えや経験の浅い人の提案を、内容の比較より先に退ける回答が高く出ました。",
    ["新案に過去の失敗例だけを返す", "流行や若さを理由に検討対象から外す"],
    ["新案の良い点を一つ挙げてから、懸念を事実と条件で伝える"],
    "新しい企画書を手にしたまま地層のような椅子に座る人物のイラスト",
  ),
  definition(
    "permanent_victim",
    "永久被害者ポジション",
    [
      "反省会の席順で、自分だけ毎回ゲスト枠になっています。",
      "問題の矢印はよく見えますが、自分向きの一本だけ非表示です。",
      "事情説明が長編化。改善できる一手はエンドロール後に登場します。",
    ],
    "揉め事や失敗で自分が変えられた点を探すより、環境や相手の問題を先に挙げる回答が目立ちました。",
    ["失敗の説明を相手や条件から始める", "謝る前に自分の事情を長く説明する"],
    ["出来事ごとに「次回、自分が変えられる一手」を一つだけ記録する"],
    "多数の矢印を外へ向け、自分向きの矢印だけ隠す人物のイラスト",
  ),
  definition(
    "logic_pressure_cooker",
    "正論圧力鍋",
    [
      "正しい内容に高圧設定が追加されています。",
      "誤りを直す速度は最速。ただし相手の受信機が先に故障しそうです。",
      "事実の補足がいつの間にか公開説教へ。火力調整つまみを探してください。",
    ],
    "誤りを見つけたとき、穏やかな補足や距離を置く選択より、強い指摘や公開批判を選ぶ傾向が出ました。",
    ["人前で誤りを強い言葉で指摘する", "反論を相手が従うまで続ける"],
    ["指摘前に目的を「訂正」「確認」「制裁」から選び、制裁なら投稿を保留する"],
    "圧力計のついた鍋から正論の吹き出しが勢いよく出るイラスト",
  ),
  definition(
    "approval_reformer",
    "世直し承認モンスター",
    [
      "社会を正す投稿に、拍手の通知も同時発注しています。",
      "問題の改善より態度表明が先着。正しさの観客席が満員です。",
      "その拡散、本当に解決用ですか。それとも正しい自分の点呼でしょうか。",
    ],
    "事実確認や改善への働きかけより、批判への参加や周囲への態度表明を促す回答が高く出ました。",
    [
      "批判への賛同を公開し周囲にも反応を求める",
      "正しい立場にいることを繰り返し示す",
    ],
    ["投稿前に、当事者に届く具体的な改善が一つ含まれるか確認する"],
    "拡声器で正義を語りながら通知の拍手を確認する人物のイラスト",
  ),
  definition(
    "early_rogai",
    "老害先取り型",
    [
      "若さより先に価値観がベテラン化。「最近の若い人は」と言う準備だけは完璧です。",
      "新案を開く前に却下印、説明を聞く前に説教が起動しています。",
      "経験値は高めでも、入力欄が読み取り専用になりかけています。",
    ],
    "新しい提案を早く退ける傾向と、誤りを強い言葉で正そうとする傾向が同時に高く出ました。",
    [
      "新案を過去の経験で即座に否定する",
      "経験の浅い相手へ強い口調で結論を伝える",
    ],
    ["反論の前に相手の提案を自分の言葉で要約し、合っているか確認する"],
    "新しい企画書へ却下印を押しながら説教する人物のイラスト",
  ),
  definition(
    "humanity_abandoned",
    "人間諦めモード",
    [
      "人類への期待値を下げすぎて、優しさまで省エネ対象になっています。",
      "どうせ裏がある、と決めた瞬間に相手の事情までログアウトしています。",
      "期待しない防具は頑丈ですが、応援の通知も全部遮断中です。",
    ],
    "相手の事情に付き合う余裕の低下と、善意や挑戦を疑う反応が重なって高く出ました。",
    ["相談を甘えとして早く切り上げる", "挑戦の話を失敗前提で受け取る"],
    ["一週間に一度、結果に関係なく他人の具体的な努力を一つ言葉にする"],
    "人類への期待メーターを下げ、優しさの電源も切りかける人物のイラスト",
  ),
  definition(
    "elite_lecturer",
    "選民系正論マン",
    [
      "正しさだけでなく、正しい自分の席順まで確認しています。",
      "社会への提言に、なぜか自分専用の優先席が付属しています。",
      "みんなのルールを語りながら、自分の例外だけ脚注に追加中です。",
    ],
    "自分への特別扱いを求める傾向と、正しい立場を周囲へ示す傾向が同時に高く出ました。",
    ["立場を示して例外対応を求める", "批判への参加を周囲にも促す"],
    ["主張の前に、自分にも同じ基準と負担を適用できるか確認する"],
    "演台の上からルールを語りつつ自分用の優先席を確保する人物のイラスト",
  ),
  definition(
    "counterattack_defense",
    "逆ギレ防衛型",
    [
      "守りに入ったはずが、なぜか周囲が全員ダメージを受けています。",
      "指摘を受けると説明より反撃が先行。防御ボタンの配置が逆です。",
      "自分の事情を守るための正論が、相手への連続攻撃になっています。",
    ],
    "問題を外側へ帰属する傾向と、指摘や対立に強い言葉で応じる傾向が重なって出ました。",
    [
      "指摘されると相手の欠点を挙げ返す",
      "自分の事情を説明しながら相手を責める",
    ],
    ["反論前に、同意できる事実と自分が直せる点を一つずつ伝える"],
    "盾を構えたまま前方へ衝撃波を放つ人物を描いたイラスト",
  ),
  definition(
    "natural_controller",
    "ナチュラル支配者",
    [
      "お願いの形をしていますが、相手側に断るボタンがありません。",
      "自分なら頼んで当然、相手なら引き受けて当然の二段構えです。",
      "丁寧な依頼文の裏で、例外扱いと誘導が同時ログインしています。",
    ],
    "自分への特別対応を期待する傾向と、相手が断りにくい形で依頼を進める傾向が高く出ました。",
    ["立場を添えて依頼の優先度を上げる", "了承後に負担や条件を追加する"],
    ["依頼の全条件と断る選択肢を最初に示し、返答期限にも余裕を持たせる"],
    "断るボタンのない依頼画面を相手に差し出す人物のイラスト",
  ),
  definition(
    "flame_commentator",
    "炎上観戦評論家",
    [
      "火を消すより、誰が燃えるべきかの解説に忙しいようです。",
      "事実確認は後半戦。まず観客席から正しい側のユニフォームを掲げます。",
      "炎上を嫌いながら、実況と採点で滞在時間だけ伸ばしています。",
    ],
    "人の失敗を冷めた目で見る傾向と、批判の場で正しい立場を示す傾向が同時に高く出ました。",
    ["炎上中の人を評価する投稿を続ける", "事実が揃う前に批判側へ加わる"],
    ["炎上を見たら、追加情報を生まない感想投稿は24時間保留する"],
    "燃える投稿を観客席からマイクで実況する人物のイラスト",
  ),
  definition(
    "zero_margin_defense",
    "余裕ゼロ自己防衛型",
    [
      "心の空き容量がなく、他人の事情から先に削除されています。",
      "疲労アラートの処理を、相手のせいというフォルダへ自動保存中です。",
      "弱音を受信すると責任判定が起動。まず自分の残量表示をご確認ください。",
    ],
    "他人のつらさに付き合う余裕の低下と、問題の責任を外へ置く傾向が重なって高く出ました。",
    [
      "相談を負担として早く切り上げる",
      "余裕がない場面で相手の問題点を先に挙げる",
    ],
    ["余裕がないことを先に伝え、返答できる時間を具体的に提案する"],
    "空き容量ゼロの心を示し、他人の事情のファイルを閉じる人物のイラスト",
  ),
  definition(
    "good_old_days_reserve",
    "昔はよかった予備軍",
    [
      "まだ昔ではない出来事まで「昔はよかった」に分類し始めています。",
      "新しい案への評価が、内容より製造年で決まりかけています。",
      "最新情報を疑う目は鋭いですが、過去の記憶には甘口採点です。",
    ],
    "新しい考えを過去基準で退ける傾向と、人の挑戦の価値を低く見積もる傾向が高く出ました。",
    ["新しい案を流行として片づける", "挑戦の宣言を続かないものとして扱う"],
    ["過去案と新案を、目的、費用、効果の同じ三項目で比較する"],
    "新しい資料を古いアルバムの分類棚へ入れようとする人物のイラスト",
  ),
  definition(
    "instant_mount",
    "マウント即決型",
    [
      "会話が始まる前に、上下関係だけ先にロードされています。",
      "普通に頼める場面でも、肩書と強めの口調が先発メンバーです。",
      "相手の返答を待つより先に、自分が上という結論だけ確定しています。",
    ],
    "自分への特別扱いを求める傾向と、強い言葉で相手を従わせる傾向が同時に高く出ました。",
    [
      "立場を示して強い口調で対応を求める",
      "会話の序盤で相手の経験や理解を低く扱う",
    ],
    ["肩書や実績を出さず、必要な事実と希望だけで一度依頼する"],
    "会話画面より先に上下関係のメーターを表示する人物のイラスト",
  ),
  definition(
    "emotion_cost_manager",
    "感情コスパ経営者",
    [
      "人の気持ちを、費用対効果の悪いタスクとして処理しています。",
      "共感は削減対象、誘導は効率化。人間関係の決算が心配です。",
      "相手の事情を省略すると会話は速いですが、信頼残高も同時に減ります。",
    ],
    "相手の事情への関心が薄れる傾向と、相手を目的達成のために誘導する傾向が重なりました。",
    ["相手の負担を確認せず依頼を進める", "感情の説明を非効率として切り上げる"],
    ["依頼前に相手の負担と得られる選択肢を声に出して確認する"],
    "感情を表計算のコスト欄で管理しようとする人物のイラスト",
  ),
  definition(
    "online_executioner",
    "ネット私刑執行人",
    [
      "判決、執行、拡散までワンオペで担当しています。",
      "訂正の目的地を通過して、制裁と観客集めまで直行しています。",
      "正しさのアクセルは全開。事実確認と影響範囲が置いていかれています。",
    ],
    "誤りを公開で強く指摘する傾向と、批判への参加や拡散を正当化する傾向が高く出ました。",
    ["誤った投稿を引用して批判を広げる", "周囲にも批判への参加を促す"],
    ["公開反応の前に、訂正に必要な最小限の相手と範囲を選ぶ"],
    "判決印と拡散ボタンを同時に操作する人物を描いたイラスト",
  ),
  definition(
    "personal_exception",
    "自分だけ例外申請中",
    [
      "ルールは大切。ただし自分の事情は毎回、特別審査です。",
      "うまくいかない理由は外部提出、自分の改善点は申請書から抜けています。",
      "同じ条件を求めつつ、自分の欄だけ「事情あり」に自動チェックです。",
    ],
    "自分への例外を求める傾向と、問題の責任を環境や相手へ置く傾向が同時に高く出ました。",
    [
      "断られた理由を相手の対応不足として扱う",
      "自分の事情を理由にルール変更を求める",
    ],
    ["例外を頼むときは、代替案と自分が引き受ける負担も同時に示す"],
    "自分の申請書だけ特別審査の窓口へ差し出す人物のイラスト",
  ),
] as const satisfies readonly ResultDefinition[];

export const RESULT_DEFINITIONS = RESULT_DEFINITIONS_V1;

export const SINGLE_RESULTS = {
  empathy_erosion: "emotional_power_saver",
  cynicism_envy: "cynical_watcher",
  entitlement_superiority: "natural_elite",
  manipulation_exploitation: "human_chess_player",
  rigidity_intolerance: "premature_fossil",
  blame_justification: "permanent_victim",
  aggression_control: "logic_pressure_cooker",
  moral_grandstanding: "approval_reformer",
} as const satisfies Readonly<Record<FacetKey, ResultTypeKey>>;

export const PAIR_RESULTS = {
  "aggression_control|rigidity_intolerance": "early_rogai",
  "cynicism_envy|empathy_erosion": "humanity_abandoned",
  "entitlement_superiority|moral_grandstanding": "elite_lecturer",
  "aggression_control|blame_justification": "counterattack_defense",
  "entitlement_superiority|manipulation_exploitation": "natural_controller",
  "cynicism_envy|moral_grandstanding": "flame_commentator",
  "blame_justification|empathy_erosion": "zero_margin_defense",
  "cynicism_envy|rigidity_intolerance": "good_old_days_reserve",
  "aggression_control|entitlement_superiority": "instant_mount",
  "empathy_erosion|manipulation_exploitation": "emotion_cost_manager",
  "aggression_control|moral_grandstanding": "online_executioner",
  "blame_justification|entitlement_superiority": "personal_exception",
} as const satisfies Readonly<Record<string, ResultTypeKey>>;

const toPairKey = (first: FacetKey, second: FacetKey): string =>
  [first, second].sort().join("|");

export const resolveResultType = (
  rankedFacets: readonly { key: FacetKey; current: number; change: number }[],
  totalScore: number,
): ResultTypeKey => {
  if (rankedFacets.length !== FACET_KEYS.length) {
    throw new Error(`Expected ${FACET_KEYS.length} ranked facets`);
  }
  if (totalScore <= 24 && rankedFacets.every((facet) => facet.current < 50)) {
    return "internet_unscarred";
  }

  const [first, second] = rankedFacets as readonly [
    { key: FacetKey; current: number; change: number },
    { key: FacetKey; current: number; change: number },
    ...{ key: FacetKey; current: number; change: number }[],
  ];
  if (first.current - second.current >= 20) return SINGLE_RESULTS[first.key];

  return (
    (PAIR_RESULTS as Readonly<Record<string, ResultTypeKey>>)[
      toPairKey(first.key, second.key)
    ] ?? SINGLE_RESULTS[first.key]
  );
};
